<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import OnboardingAPI from 'dashboard/api/onboarding';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { resolveTargetInbox } from './resolveTargetInbox';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '18:00';
const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

const dayShortNames = {
  0: t('ONBOARDING.STEP_5.DAYS_SHORT.0'),
  1: t('ONBOARDING.STEP_5.DAYS_SHORT.1'),
  2: t('ONBOARDING.STEP_5.DAYS_SHORT.2'),
  3: t('ONBOARDING.STEP_5.DAYS_SHORT.3'),
  4: t('ONBOARDING.STEP_5.DAYS_SHORT.4'),
  5: t('ONBOARDING.STEP_5.DAYS_SHORT.5'),
  6: t('ONBOARDING.STEP_5.DAYS_SHORT.6'),
};

const timeZones = timeZoneOptions();

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(5)
);

const inboxId = ref(null);
const selectedDays = ref([]);
const startTime = ref(DEFAULT_START_TIME);
const endTime = ref(DEFAULT_END_TIME);
const timezone = ref(DEFAULT_TIMEZONE);
const saveError = ref(null);

const pad = value => String(value).padStart(2, '0');

// The onboarding form applies a single shared time range to every selected day —
// unlike the full inbox settings page, which allows a different range per day.
const applyInboxWorkingHours = inbox => {
  const activeDays = (inbox.working_hours || []).filter(
    day => !day.closed_all_day
  );
  if (activeDays.length) {
    const [first] = activeDays;
    selectedDays.value = activeDays.map(day => day.day_of_week);
    startTime.value = `${pad(first.open_hour)}:${pad(first.open_minutes)}`;
    endTime.value = `${pad(first.close_hour)}:${pad(first.close_minutes)}`;
  } else {
    selectedDays.value = [];
    startTime.value = DEFAULT_START_TIME;
    endTime.value = DEFAULT_END_TIME;
  }
  timezone.value = inbox.timezone || DEFAULT_TIMEZONE;
};

const timezoneLabel = value =>
  timeZones.find(option => option.value === value)?.label || value;

const isValid = computed(
  () =>
    selectedDays.value.length > 0 &&
    !!startTime.value &&
    !!endTime.value &&
    endTime.value > startTime.value
);

// Reacts to every change, not just successful saves — so a mid-typing invalid
// range (e.g. end before start) disables Next immediately, without waiting on
// a network round-trip.
watch(isValid, value => emit('update:can-advance', value), { immediate: true });

const buildDaysPayload = () => {
  const [startHour, startMinute] = startTime.value.split(':').map(Number);
  const [endHour, endMinute] = endTime.value.split(':').map(Number);

  return [0, 1, 2, 3, 4, 5, 6].map(day => {
    const isSelected = selectedDays.value.includes(day);
    return {
      day_of_week: day,
      closed_all_day: !isSelected,
      open_hour: isSelected ? startHour : '',
      open_minutes: isSelected ? startMinute : '',
      close_hour: isSelected ? endHour : '',
      close_minutes: isSelected ? endMinute : '',
      open_all_day: false,
    };
  });
};

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 5])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

// Every field is saved together — the endpoint takes the full week + timezone
// in one PATCH, so there's no separate per-field payload like Step03Teams'
// name/description/members.
const persistWorkingHours = async () => {
  if (!inboxId.value) return;
  try {
    await InboxesAPI.update(inboxId.value, {
      working_hours: buildDaysPayload(),
      working_hours_enabled: selectedDays.value.length > 0,
      timezone: timezone.value,
    });
    saveError.value = null;
    if (isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    saveError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_5.UPDATE_ERROR');
  }
};

// Days are saved as soon as they're toggled — even clearing the last selected
// day is saved right away, so the real inbox always matches what's shown here.
const toggleDay = day => {
  selectedDays.value = selectedDays.value.includes(day)
    ? selectedDays.value.filter(d => d !== day)
    : [...selectedDays.value, day];
  persistWorkingHours();
};

const attemptSaveTime = () => {
  if (!isValid.value) return;
  persistWorkingHours();
};

const onTimezoneChange = value => {
  timezone.value = value;
  if (isValid.value) persistWorkingHours();
};

// Re-syncs when the inbox's working hours are changed from another screen
// (e.g. Settings > Inbox > Business hours) so both views stay live.
const refreshFromServer = async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (!inbox) return;

    inboxId.value = inbox.id;
    applyInboxWorkingHours(inbox);
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (inbox) {
      inboxId.value = inbox.id;
      applyInboxWorkingHours(inbox);
    }
    if (isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_5.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/417-horario-de-atendimento-humano-quando-existe-alguem-para-receber"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_5.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_5.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.REQUIRED_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_5.DESCRIPTION') }}</p>

      <div class="flex flex-col w-full max-w-2xl gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ONBOARDING.STEP_5.DAYS_LABEL') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="day in DAY_ORDER"
              :key="day"
              type="button"
              class="px-4 py-2 text-sm font-medium border rounded-full"
              :class="
                selectedDays.includes(day)
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggleDay(day)"
            >
              {{ dayShortNames[day] }}
            </button>
          </div>
        </div>

        <div class="flex gap-4">
          <Input
            v-model="startTime"
            type="time"
            custom-input-class="no-margin"
            :label="t('ONBOARDING.STEP_5.START_TIME_LABEL')"
            @blur="attemptSaveTime"
          />
          <Input
            v-model="endTime"
            type="time"
            custom-input-class="no-margin"
            :label="t('ONBOARDING.STEP_5.END_TIME_LABEL')"
            @blur="attemptSaveTime"
          />
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ONBOARDING.STEP_5.TIMEZONE_LABEL') }}
          </span>
          <SelectMenu
            v-model="timezone"
            :options="timeZones"
            :label="timezoneLabel(timezone)"
            size="md"
            trigger-class="max-w-full w-72"
            @update:model-value="onTimezoneChange"
          />
        </div>

        <p v-if="saveError" class="text-sm text-n-ruby-9">{{ saveError }}</p>
      </div>
    </div>
  </div>
</template>
